import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import ShiftTemplatesManager from '@/components/ShiftTemplatesManager';
import { openPrintWindow } from '@/utils/printWindow';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Calendar, ChevronLeft, ChevronRight, Plus, Clock, User, 
  Trash2, Edit, Users, CalendarDays, Building, AlertTriangle, CheckCircle, Download, Printer
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
import RatioHeatmapWeek from '@/components/RatioHeatmapWeek';
// API imported from config;

const SHIFT_COLORS = [
  'bg-blue-100 border-blue-300 text-blue-800',
  'bg-green-100 border-green-300 text-green-800',
  'bg-purple-100 border-purple-300 text-purple-800',
  'bg-amber-100 border-amber-300 text-amber-800',
  'bg-pink-100 border-pink-300 text-pink-800',
  'bg-cyan-100 border-cyan-300 text-cyan-800',
  'bg-orange-100 border-orange-300 text-orange-800',
];

const StaffSchedulePage = () => {
  const { getAuthHeaders } = useAuth();
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    return monday.toISOString().split('T')[0];
  });
  const [scheduleData, setScheduleData] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [ratios, setRatios] = useState(null);
  // Date used for ratio preview (lets admin check coverage for any future day)
  const [ratioDate, setRatioDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [shiftTimes, setShiftTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadStartDate, setDownloadStartDate] = useState('');
  const [downloadEndDate, setDownloadEndDate] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [viewMode, setViewMode] = useState('classroom'); // 'classroom' or 'staff'
  const [shiftForm, setShiftForm] = useState({
    staff_id: '',
    classroom_id: '',
    date: '',
    start_time: '08:00',
    end_time: '17:00',
    role: 'General',
    notes: ''
  });

  const fetchSchedule = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/schedule/weekly?week_start=${weekStart}`, getAuthHeaders());
      setScheduleData(res.data);
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [weekStart, getAuthHeaders]);

  const fetchStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/staff`, getAuthHeaders());
      setStaffList(res.data);
    } catch (error) {
      console.error('Failed to load staff');
    }
  }, [getAuthHeaders]);

  const fetchClassrooms = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/classrooms`, getAuthHeaders());
      setClassrooms(res.data);
    } catch (error) {
      console.error('Failed to load classrooms');
    }
  }, [getAuthHeaders]);

  const fetchRatios = useCallback(async () => {
    try {
      // Schedule page is forward-looking — use roster (all assigned children),
      // not live (checked-in only). Live mode is used by Dashboard widgets.
      const res = await axios.get(
        `${API}/classrooms/ratios?date=${ratioDate}&mode=roster`,
        getAuthHeaders(),
      );
      setRatios(res.data);
    } catch (error) {
      console.error('Failed to load ratios');
    }
  }, [getAuthHeaders, ratioDate]);

  const fetchShiftTimes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/shift-times`, getAuthHeaders());
      setShiftTimes(res.data || []);
    } catch (error) {
      console.error('Failed to load shift times');
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchSchedule();
    fetchStaff();
    fetchClassrooms();
    fetchRatios();
    fetchShiftTimes();
  }, [fetchSchedule, fetchStaff, fetchClassrooms, fetchRatios, fetchShiftTimes]);

  const navigateWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction === 'next' ? 7 : -7));
    setWeekStart(current.toISOString().split('T')[0]);
    setLoading(true);
  };

  const goToToday = () => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    setWeekStart(monday.toISOString().split('T')[0]);
    setLoading(true);
  };

  const openAddShift = (classroomId, staffId, date) => {
    setEditingShift(null);
    setShiftForm({
      staff_id: staffId || '',
      classroom_id: classroomId || '',
      date: date || '',
      start_time: '08:00',
      end_time: '17:00',
      role: 'General',
      notes: ''
    });
    setShiftDialogOpen(true);
  };

  const openEditShift = (shift) => {
    setEditingShift(shift);
    setShiftForm({
      staff_id: shift.staff_id,
      classroom_id: shift.classroom_id || '',
      date: shift.date,
      start_time: shift.start_time,
      end_time: shift.end_time,
      role: shift.role || 'General',
      notes: shift.notes || ''
    });
    setShiftDialogOpen(true);
  };

  const handleSaveShift = async (e) => {
    e.preventDefault();
    
    if (!shiftForm.staff_id || !shiftForm.date) {
      toast.error('Please select staff and date');
      return;
    }

    try {
      if (editingShift) {
        await axios.put(`${API}/schedule/shifts/${editingShift.id}`, shiftForm, getAuthHeaders());
        toast.success('Shift updated');
      } else {
        await axios.post(`${API}/schedule/shifts`, shiftForm, getAuthHeaders());
        toast.success('Shift created');
      }
      setShiftDialogOpen(false);
      fetchSchedule();
      fetchRatios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save shift');
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) return;
    
    try {
      await axios.delete(`${API}/schedule/shifts/${shiftId}`, getAuthHeaders());
      toast.success('Shift deleted');
      fetchSchedule();
      fetchRatios();
    } catch (error) {
      toast.error('Failed to delete shift');
    }
  };

  const getStaffColor = (staffId) => {
    const index = staffList.findIndex(s => s.id === staffId);
    return SHIFT_COLORS[index >= 0 ? index % SHIFT_COLORS.length : 0];
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const getShiftDuration = (start, end) => {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const duration = (eh * 60 + em) - (sh * 60 + sm);
    const hours = Math.floor(duration / 60);
    const mins = duration % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Get all shifts for a specific classroom and day
  const getClassroomShifts = (classroomId, date) => {
    if (!scheduleData?.staff) return [];
    
    // Get classroom name for matching (some shifts store name instead of id)
    const classroom = classrooms.find(c => c.id === classroomId);
    const classroomName = classroom?.name;
    
    const shifts = [];
    scheduleData.staff.forEach(staff => {
      if (!staff.shifts) return;
      staff.shifts.forEach(shift => {
        // Match by classroom_id (UUID) OR classroom_id equals classroom name (legacy) OR classroom equals classroomId
        const matchesClassroom = (
          shift.classroom_id === classroomId || 
          shift.classroom === classroomId ||
          (classroomName && shift.classroom_id === classroomName)
        );
        if (matchesClassroom && shift.date === date) {
          shifts.push({ ...shift, staff_name: staff.name });
        }
      });
    });
    return shifts;
  };

  const handleDownloadSchedule = async (format) => {
    if (!downloadStartDate || !downloadEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    setDownloadLoading(true);
    try {
      const res = await axios.get(
        `${API}/schedule/export?start_date=${downloadStartDate}&end_date=${downloadEndDate}&format=${format}&view=${viewMode}`,
        {
          ...getAuthHeaders(),
          responseType: format === 'pdf' ? 'blob' : 'json'
        }
      );
      
      if (format === 'pdf') {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `schedule_${downloadStartDate}_to_${downloadEndDate}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success('Schedule downloaded!');
      } else {
        // CSV format
        const blob = new Blob([res.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `schedule_${downloadStartDate}_to_${downloadEndDate}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success('Schedule downloaded!');
      }
      setDownloadDialogOpen(false);
    } catch (error) {
      toast.error('Failed to download schedule');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handlePrintSchedule = () => {
    // Set print date range to current view
    const endDate = new Date(weekStart);
    endDate.setDate(endDate.getDate() + 6);
    
    // Open print dialog with current week
    const printContent = document.getElementById('schedule-print-area');
    if (printContent) {
      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Staff Schedule - Week of ${new Date(weekStart).toLocaleDateString()}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h1 { font-size: 24px; margin-bottom: 10px; }
              h2 { font-size: 18px; color: #666; margin-bottom: 20px; }
              table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
              th { background-color: #f5f5f5; font-weight: bold; }
              .classroom-header { background-color: #e8f4fd; font-weight: bold; }
              .shift { padding: 4px; margin: 2px 0; background: #f0f0f0; border-radius: 4px; }
              @media print { body { padding: 0; } }
            </style>
          </head>
          <body>
            <h1>Staff Schedule</h1>
            <h2>Week of ${new Date(weekStart).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</h2>
            ${generatePrintableSchedule()}
          </body>
        </html>
      `;
      openPrintWindow(html);
    }
  };

  const generatePrintableSchedule = () => {
    if (!scheduleData || !classrooms.length) return '<p>No schedule data available</p>';
    
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const weekDates = days.map((_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d.toISOString().split('T')[0];
    });
    
    let html = '<table><thead><tr><th>Classroom</th>';
    weekDates.forEach((date, i) => {
      const d = new Date(date);
      html += `<th>${days[i]} ${d.getMonth() + 1}/${d.getDate()}</th>`;
    });
    html += '</tr></thead><tbody>';
    
    classrooms.forEach(classroom => {
      html += `<tr><td class="classroom-header">${classroom.name}</td>`;
      weekDates.forEach(date => {
        const shifts = getClassroomShifts(classroom.id, date);
        html += '<td>';
        if (shifts.length > 0) {
          shifts.forEach(shift => {
            html += `<div class="shift">${shift.staff_name}<br/>${shift.start_time}-${shift.end_time}</div>`;
          });
        } else {
          html += '-';
        }
        html += '</td>';
      });
      html += '</tr>';
    });
    
    html += '</tbody></table>';
    return html;
  };

  if (loading) {
    return (
      <DashboardLayout title="Staff Schedule">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Staff Schedule">
      <div className="space-y-6" data-testid="staff-schedule-page">
        {/* Header with Navigation */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl">Staff Schedule</h1>
            <p className="text-muted-foreground">
              Week of {new Date(weekStart).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => navigateWeek('prev')}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigateWeek('next')}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            
            <Dialog open={shiftDialogOpen} onOpenChange={setShiftDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => openAddShift('', '', '')} data-testid="add-shift-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Shift
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading">
                    {editingShift ? 'Edit Shift' : 'Add Shift'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveShift} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Classroom *</Label>
                    <Select 
                      value={shiftForm.classroom_id} 
                      onValueChange={(v) => setShiftForm({...shiftForm, classroom_id: v})}
                    >
                      <SelectTrigger data-testid="shift-classroom-select">
                        <SelectValue placeholder="Select classroom" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned / Float</SelectItem>
                        {classrooms.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name} ({c.age_range || 'All ages'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Staff Member *</Label>
                    <Select 
                      value={shiftForm.staff_id} 
                      onValueChange={(v) => setShiftForm({...shiftForm, staff_id: v})}
                    >
                      <SelectTrigger data-testid="shift-staff-select">
                        <SelectValue placeholder="Select staff member" />
                      </SelectTrigger>
                      <SelectContent>
                        {staffList.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.user_name || 'Unknown'} - {s.position}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={shiftForm.date}
                      onChange={(e) => setShiftForm({...shiftForm, date: e.target.value})}
                      required
                      data-testid="shift-date-input"
                    />
                  </div>
                  
                  {/* Shift Time Quick Select */}
                  {shiftTimes.length > 0 && (
                    <div className="space-y-2">
                      <Label>Quick Select Shift</Label>
                      <div className="flex flex-wrap gap-2">
                        {shiftTimes.map((st) => (
                          <Button
                            key={st.id}
                            type="button"
                            variant={shiftForm.start_time === st.start_time && shiftForm.end_time === st.end_time ? 'default' : 'outline'}
                            size="sm"
                            className="text-xs"
                            style={{ 
                              borderColor: st.color,
                              backgroundColor: shiftForm.start_time === st.start_time && shiftForm.end_time === st.end_time ? st.color : 'transparent',
                              color: shiftForm.start_time === st.start_time && shiftForm.end_time === st.end_time ? 'white' : 'inherit'
                            }}
                            onClick={() => setShiftForm({
                              ...shiftForm, 
                              start_time: st.start_time, 
                              end_time: st.end_time
                            })}
                            data-testid={`quick-shift-${st.id}`}
                          >
                            {st.name}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Time *</Label>
                      <Input
                        type="time"
                        value={shiftForm.start_time}
                        onChange={(e) => setShiftForm({...shiftForm, start_time: e.target.value})}
                        required
                        data-testid="shift-start-time"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Time *</Label>
                      <Input
                        type="time"
                        value={shiftForm.end_time}
                        onChange={(e) => setShiftForm({...shiftForm, end_time: e.target.value})}
                        required
                        data-testid="shift-end-time"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Role/Area</Label>
                    <Select 
                      value={shiftForm.role} 
                      onValueChange={(v) => setShiftForm({...shiftForm, role: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="General">General</SelectItem>
                        <SelectItem value="Lead Teacher">Lead Teacher</SelectItem>
                        <SelectItem value="Assistant">Assistant</SelectItem>
                        <SelectItem value="Float">Float</SelectItem>
                        <SelectItem value="Opening">Opening</SelectItem>
                        <SelectItem value="Closing">Closing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={shiftForm.notes}
                      onChange={(e) => setShiftForm({...shiftForm, notes: e.target.value})}
                      placeholder="Any additional notes..."
                    />
                  </div>
                  
                  <div className="flex justify-between gap-3">
                    {editingShift && (
                      <Button 
                        type="button" 
                        variant="destructive"
                        onClick={() => {
                          handleDeleteShift(editingShift.id);
                          setShiftDialogOpen(false);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    )}
                    <div className="flex gap-3 ml-auto">
                      <Button type="button" variant="outline" onClick={() => setShiftDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" data-testid="save-shift-btn">
                        {editingShift ? 'Update Shift' : 'Create Shift'}
                      </Button>
                    </div>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Shift Templates Manager */}
        <ShiftTemplatesManager 
          getAuthHeaders={getAuthHeaders}
          staffList={staffList}
          classrooms={classrooms}
          onTemplateApplied={(firstDate) => {
            // Navigate to the week where shifts were created
            if (firstDate) {
              const shiftDate = new Date(firstDate);
              const monday = new Date(shiftDate);
              monday.setDate(shiftDate.getDate() - shiftDate.getDay() + 1);
              setWeekStart(monday.toISOString().split('T')[0]);
            }
            fetchSchedule();
            fetchRatios();
          }}
        />

        {/* Ratio Summary Cards */}
        {ratios && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="card-clay">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Building className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{ratios.summary?.total_classrooms || 0}</p>
                    <p className="text-sm text-muted-foreground">Classrooms</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-clay">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{ratios.summary?.total_children || 0}</p>
                    <p className="text-sm text-muted-foreground">Children Enrolled</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-clay">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <User className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{ratios.summary?.total_staff_scheduled || 0}</p>
                    <p className="text-sm text-muted-foreground">Staff Scheduled Today</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={`card-clay ${ratios.summary?.compliant_classrooms === ratios.summary?.total_classrooms ? 'border-green-300' : 'border-amber-300'}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${ratios.summary?.compliant_classrooms === ratios.summary?.total_classrooms ? 'bg-green-100' : 'bg-amber-100'}`}>
                    {ratios.summary?.compliant_classrooms === ratios.summary?.total_classrooms ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{ratios.summary?.compliance_rate || '0%'}</p>
                    <p className="text-sm text-muted-foreground">DCF Compliant</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* View Mode Tabs */}
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList>
            <TabsTrigger value="classroom">
              <Building className="w-4 h-4 mr-2" />
              By Classroom
            </TabsTrigger>
            <TabsTrigger value="staff">
              <User className="w-4 h-4 mr-2" />
              By Staff
            </TabsTrigger>
          </TabsList>

          {/* Classroom View */}
          <TabsContent value="classroom">
            {/* Download/Print Controls - Prominent Position */}
            <Card className="card-clay mb-4">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Building className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Classrooms:</span>
                    {classrooms.map((c) => (
                      <Badge key={c.id} variant="outline" className="text-xs">
                        {c.name}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Print Current Week */}
                    <Button variant="outline" onClick={handlePrintSchedule}>
                      <Printer className="w-4 h-4 mr-2" />
                      Print Week
                    </Button>
                    
                    {/* Download Dialog */}
                    <Dialog open={downloadDialogOpen} onOpenChange={setDownloadDialogOpen}>
                      <DialogTrigger asChild>
                        <Button onClick={() => {
                          const endDate = new Date(weekStart);
                          endDate.setDate(endDate.getDate() + 6);
                          setDownloadStartDate(weekStart);
                          setDownloadEndDate(endDate.toISOString().split('T')[0]);
                        }}>
                          <Download className="w-4 h-4 mr-2" />
                          Download Schedule
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle className="font-heading">Download Classroom Schedule</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Start Date</Label>
                              <Input
                                type="date"
                                value={downloadStartDate}
                                onChange={(e) => setDownloadStartDate(e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>End Date</Label>
                              <Input
                                type="date"
                                value={downloadEndDate}
                                onChange={(e) => setDownloadEndDate(e.target.value)}
                              />
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Classrooms included: {classrooms.map(c => c.name).join(', ')}
                          </p>
                          <div className="flex gap-2">
                            <Button 
                              className="flex-1" 
                              variant="outline"
                              onClick={() => handleDownloadSchedule('csv')}
                              disabled={downloadLoading}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              CSV (Excel)
                            </Button>
                            <Button 
                              className="flex-1"
                              onClick={() => handleDownloadSchedule('pdf')}
                              disabled={downloadLoading}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              PDF
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-clay overflow-hidden" id="schedule-print-area">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="p-3 text-left font-semibold w-48 border-b">
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4" />
                            Classroom
                          </div>
                        </th>
                        {scheduleData?.days?.map((day) => (
                          <th 
                            key={day.date} 
                            className={`p-3 text-center border-b border-l ${
                              day.date === new Date().toISOString().split('T')[0] 
                                ? 'bg-primary/10' 
                                : ''
                            }`}
                          >
                            <div className="font-semibold">{day.day_short}</div>
                            <div className="text-lg font-bold">{day.day_num}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {classrooms.map((classroom) => {
                        const ratioInfo = ratios?.classrooms?.find(r => r.classroom_id === classroom.id);
                        
                        return (
                          <tr key={classroom.id} className="border-b hover:bg-muted/30">
                            <td className="p-3 border-r">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{classroom.name}</p>
                                  {ratioInfo && (
                                    <Badge 
                                      variant={ratioInfo.is_compliant ? 'default' : 'destructive'}
                                      className={`text-xs ${!ratioInfo.is_compliant ? 'cursor-pointer hover:opacity-80' : ''}`}
                                      onClick={() => !ratioInfo.is_compliant && openAddShift(classroom.id, '', new Date().toISOString().split('T')[0])}
                                      data-testid={`need-staff-badge-${classroom.id}`}
                                    >
                                      {ratioInfo.is_compliant ? 'OK' : `Need ${ratioInfo.required_staff - ratioInfo.staff_count} ➕`}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">{classroom.age_range}</p>
                                {ratioInfo && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {ratioInfo.child_count} kids • {ratioInfo.ratio}
                                  </p>
                                )}
                              </div>
                            </td>
                            {scheduleData?.days?.map((day) => {
                              const dayShifts = getClassroomShifts(classroom.id, day.date);
                              const isToday = day.date === new Date().toISOString().split('T')[0];
                              const needsMoreStaff = ratioInfo && !ratioInfo.is_compliant && isToday;
                              
                              return (
                                <td 
                                  key={day.date} 
                                  className={`p-2 border-l align-top min-h-[100px] cursor-pointer ${isToday ? 'bg-primary/5' : ''} ${needsMoreStaff ? 'bg-red-50' : ''}`}
                                  onClick={() => openAddShift(classroom.id, '', day.date)}
                                  data-testid={`cell-${classroom.id}-${day.date}`}
                                >
                                  <div className="min-h-[80px] space-y-1">
                                    {dayShifts.map((shift) => (
                                      <div
                                        key={shift.id}
                                        className={`p-2 rounded-lg border cursor-pointer text-xs ${getStaffColor(shift.staff_id)}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEditShift(shift);
                                        }}
                                        data-testid={`shift-${shift.id}`}
                                      >
                                        <div className="font-semibold truncate">{shift.staff_name}</div>
                                        <div className="text-xs opacity-80">
                                          {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                                        </div>
                                        {shift.role && shift.role !== 'General' && (
                                          <Badge variant="outline" className="mt-1 text-xs px-1 py-0">
                                            {shift.role}
                                          </Badge>
                                        )}
                                      </div>
                                    ))}
                                    {dayShifts.length === 0 ? (
                                      <div className={`h-[80px] flex items-center justify-center rounded transition-colors ${
                                        needsMoreStaff 
                                          ? 'bg-red-100 text-red-600 hover:bg-red-200 border-2 border-dashed border-red-300' 
                                          : 'text-muted-foreground/50 hover:bg-muted/50'
                                      }`}>
                                        <div className="flex flex-col items-center gap-1">
                                          <Plus className="w-5 h-5" />
                                          {needsMoreStaff && <span className="text-xs font-medium">Add Staff</span>}
                                        </div>
                                      </div>
                                    ) : (
                                      <button 
                                        type="button"
                                        className="mt-2 w-full flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 rounded py-1.5 transition-colors cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openAddShift(classroom.id, '', day.date);
                                        }}
                                        data-testid={`add-more-${classroom.id}-${day.date}`}
                                      >
                                        <Plus className="w-3 h-3 mr-1" />
                                        <span className="text-xs font-medium">Add more</span>
                                      </button>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                      {classrooms.length === 0 && (
                        <tr>
                          <td colSpan={8} className="p-8 text-center text-muted-foreground">
                            <Building className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No classrooms configured</p>
                            <p className="text-sm">Add classrooms in the Settings page first</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff View */}
          <TabsContent value="staff">
            <Card className="card-clay overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="p-3 text-left font-semibold w-48 border-b">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Staff Member
                          </div>
                        </th>
                        {scheduleData?.days?.map((day) => (
                          <th 
                            key={day.date} 
                            className={`p-3 text-center border-b border-l ${
                              day.date === new Date().toISOString().split('T')[0] 
                                ? 'bg-primary/10' 
                                : ''
                            }`}
                          >
                            <div className="font-semibold">{day.day_short}</div>
                            <div className="text-lg font-bold">{day.day_num}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {scheduleData?.staff?.map((staff, staffIndex) => (
                        <tr key={staff.id} className="border-b hover:bg-muted/30">
                          <td className="p-3 border-r">
                            <div className="flex items-center gap-2">
                              <div 
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                  SHIFT_COLORS[staffIndex % SHIFT_COLORS.length].replace('border-', 'border ')
                                }`}
                              >
                                {staff.name?.charAt(0) || 'S'}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{staff.name}</p>
                                <p className="text-xs text-muted-foreground">{staff.position}</p>
                              </div>
                            </div>
                          </td>
                          {scheduleData?.days?.map((day) => {
                            const dayShifts = (staff.shifts || []).filter(s => s.date === day.date);
                            const isToday = day.date === new Date().toISOString().split('T')[0];
                            
                            return (
                              <td 
                                key={day.date} 
                                className={`p-2 border-l align-top min-h-[80px] cursor-pointer ${isToday ? 'bg-primary/5' : ''}`}
                                onClick={() => dayShifts.length === 0 && openAddShift('', staff.id, day.date)}
                              >
                                <div className="min-h-[60px] space-y-1">
                                  {dayShifts.map((shift) => {
                                    const classroomName = classrooms.find(c => c.id === shift.classroom_id)?.name || 'Float';
                                    return (
                                      <div
                                        key={shift.id}
                                        className={`p-2 rounded-lg border cursor-pointer text-xs ${getStaffColor(staff.id)}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEditShift(shift);
                                        }}
                                        data-testid={`shift-${shift.id}`}
                                      >
                                        <div className="font-semibold truncate">{classroomName}</div>
                                        <div className="text-xs opacity-80">
                                          {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                                        </div>
                                        <div className="text-xs opacity-70">
                                          {getShiftDuration(shift.start_time, shift.end_time)}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {dayShifts.length === 0 && (
                                    <div className="h-[60px] flex items-center justify-center text-muted-foreground/50 hover:bg-muted/50 rounded">
                                      <Plus className="w-4 h-4" />
                                    </div>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      {(!scheduleData?.staff || scheduleData.staff.length === 0) && (
                        <tr>
                          <td colSpan={8} className="p-8 text-center text-muted-foreground">
                            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No staff members found</p>
                            <p className="text-sm">Add staff members in the Staff page first</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Weekly Ratio Heatmap — bird's-eye coverage view */}
        <RatioHeatmapWeek />

        {/* Classroom Ratio Details */}
        {ratios?.classrooms && ratios.classrooms.length > 0 && (
          <Card className="card-clay">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    Classroom Ratios
                  </CardTitle>
                  <CardDescription>
                    Wisconsin DCF teacher:child compliance — preview coverage for any day. Uses rostered children (not just checked-in).
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="ratio-date" className="text-sm whitespace-nowrap">Preview date:</Label>
                  <Input
                    id="ratio-date"
                    type="date"
                    value={ratioDate}
                    min="2025-01-01"
                    max="2030-12-31"
                    onChange={(e) => setRatioDate(e.target.value)}
                    className="h-8 w-40 text-sm"
                    data-testid="ratio-preview-date"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setRatioDate(new Date().toISOString().split('T')[0])}
                    className="h-8 text-xs"
                    data-testid="ratio-preview-today"
                  >
                    Today
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ratios.classrooms.map((room) => (
                  <div 
                    key={room.classroom_id}
                    className={`p-4 rounded-xl border-2 ${
                      room.is_compliant 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-red-200 bg-red-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold">{room.classroom_name}</h4>
                        <p className="text-xs text-muted-foreground">{room.age_range}</p>
                      </div>
                      {room.is_compliant ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Children:</span>
                        <span className="font-medium">{room.enrolled_children} / {room.capacity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Staff Today:</span>
                        <span className="font-medium">{room.scheduled_staff}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Required Staff:</span>
                        <span className="font-medium">{room.required_staff}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Staff Equivalent:</span>
                        <span className="font-medium">{room.staff_equivalent || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Primary Ratio:</span>
                        <span className="font-medium">{room.dcf_primary_ratio || room.dcf_required_ratio}</span>
                      </div>
                    </div>
                    
                    {!room.is_compliant && (
                      <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-700">
                        Need {room.staff_needed} more staff
                      </div>
                    )}
                    
                    {room.staff_names.length > 0 && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="text-xs text-muted-foreground mb-1">Scheduled:</p>
                        <div className="flex flex-wrap gap-1">
                          {room.staff_names.map((name, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StaffSchedulePage;
